import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Label,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@design-system';
import { AdditionalSettingsButton, useInviteClientStore } from '@/features/invite-client';

import { Clipboard, ClipboardCheck, Info, EyeOff } from 'lucide-react';

import { useState, useCallback } from 'react';

function IntakeFormLink({ url, label }: { url: string; label: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  }, [url]);

  const handleOpen = useCallback(() => {
    window.open(url, '_blank');
  }, [url]);

  return (
    <div className="grid w-full space-y-2">
      <Label>{label}</Label>
      <div className="bg-muted border-border flex w-full min-w-0 max-w-full items-center gap-2 rounded-lg border p-0.5">
        <p
          className="text-foreground min-w-0 flex-1 cursor-pointer truncate pl-2.5 text-sm hover:text-gray-950 hover:underline"
          onClick={handleCopy}
        >
          {url}
        </p>
        <div className="flex shrink-0 items-center">
          <Tooltip>
            <TooltipTrigger>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleCopy}
              >
                {copied ? <ClipboardCheck /> : <Clipboard />}
              </Button>
            </TooltipTrigger>
            <TooltipContent>{copied ? 'Copied!' : 'Copy link'}</TooltipContent>
          </Tooltip>
        </div>
      </div>
    </div>
  );
}

export function LinkTab({
  withHeader = true,
  showAdditionalSettings = true,
}: {
  withHeader?: boolean;
  showAdditionalSettings?: boolean;
}) {
  const { generateShareableLink } = useInviteClientStore();

  return (
    <div className="space-y-6">
      {withHeader && (
        <div>
          <h4 className="text-base font-semibold text-gray-950">Share via link</h4>
          <p className="text-foreground text-sm">
            Get a unique link that you can share with clients through text, social media, or in
            person
          </p>
        </div>
      )}
      <IntakeFormLink
        label="Intake Form Link"
        url={generateShareableLink()}
      />
      <Alert variant="info">
        <Info />
        <AlertTitle>Pro Tip</AlertTitle>
        <AlertDescription>
          Use this link in your existing email templates or CRM workflows. Clients can complete the
          intake form directly without needing an email invitation.
        </AlertDescription>
      </Alert>
      <Alert variant="default">
        <EyeOff />
        <AlertTitle>Testing this link?</AlertTitle>
        <AlertDescription>
          Opening this link while logged in will not show the client form. To experience it as a
          client, paste the link in an incognito/private browser window.
        </AlertDescription>
      </Alert>
      {showAdditionalSettings && <AdditionalSettingsButton className="w-full" />}
    </div>
  );
}
